<template>
  <v-dialog v-model="dialog" persistent max-width="560px">
    <v-card class="bulk-adjust-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Bulk Inventory Adjustment</div>
          <div class="dialog-subtitle">
            Update stock quantity for selected products.
          </div>
        </div>

        <v-btn icon small :disabled="working" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row">
          <div class="summary-box">
            <div class="summary-label">Selected</div>
            <div class="summary-value">{{ selectedProducts.length }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Processed</div>
            <div class="summary-value">{{ processed }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Failed / Skipped</div>
            <div class="summary-value">{{ failed + skipped }}</div>
          </div>
        </div>

        <v-form ref="form" v-model="isValid" class="mt-4">
          <div class="field-label">New Stock Quantity</div>
          <v-text-field
            v-model.number="newQty"
            type="number"
            outlined
            dense
            :disabled="working"
            :rules="[(v) => v >= 0 || 'Must be 0 or more']"
            @focus="$event.target.select()"
          />

          <div class="field-label">Reason</div>
          <v-select
            v-model="reason"
            :items="reasons"
            outlined
            dense
            :disabled="working"
            :rules="[(v) => !!v || 'Reason is required']"
          />

          <div class="field-label">Notes</div>
          <v-textarea
            v-model="notes"
            outlined
            dense
            rows="2"
            auto-grow
            :disabled="working"
          />
        </v-form>

        <div v-if="working" class="mt-4">
          <v-progress-linear
            :value="progressPercent"
            height="12"
            rounded
            color="primary"
          />

          <div class="progress-text">
            Updating products. Please wait.
          </div>
        </div>

        <v-alert
          v-if="errors.length"
          class="mt-4"
          dense
          outlined
          type="warning"
        >
          {{ errors.length }} product(s) were skipped or failed.
        </v-alert>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />

        <v-btn text :disabled="working" @click="close">
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          rounded
          dark
          :loading="working"
          :disabled="!selectedProducts.length || !isValid"
          @click="submit"
        >
          Start Adjustment
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import productService from "@/modules/Product/service.js";

export default {
  name: "BulkInventoryAdjustDialog",

  props: {
    value: Boolean,
    selectedProducts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      dialog: false,
      isValid: false,
      working: false,
      newQty: 0,
      reason: "Manual Correction",
      notes: "",
      processed: 0,
      failed: 0,
      skipped: 0,
      errors: [],
      reasons: [
        "Manual Correction",
        "Inventory Count",
        "Damaged/Lost",
        "Found Extra Stock",
        "Shrinkage/Leakage",
        "Theft",
      ],
    };
  },

  computed: {
    totalCount() {
      return this.selectedProducts.length || 1;
    },

    progressPercent() {
      return Math.min(
        100,
        Math.round(((this.processed + this.failed + this.skipped) / this.totalCount) * 100)
      );
    },
  },

  watch: {
    value(val) {
      this.dialog = val;

      if (val) {
        this.reset();
      }
    },

    dialog(val) {
      this.$emit("input", val);
    },
  },

  methods: {
    reset() {
      this.newQty = 0;
      this.reason = "Manual Correction";
      this.notes = "";
      this.processed = 0;
      this.failed = 0;
      this.skipped = 0;
      this.errors = [];
      this.working = false;
    },

    close() {
      if (this.working) return;
      this.dialog = false;
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      const confirm = await this.$swal({
        title: "Adjust selected products?",
        html: `
          <div style="text-align:left">
            <strong>Products:</strong> ${this.selectedProducts.length}<br/>
            <strong>New quantity:</strong> ${this.newQty}<br/>
            <strong>Reason:</strong> ${this.reason}
          </div>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, update",
        cancelButtonText: "No",
      });

      if (!confirm.value) return;

      this.working = true;
      this.processed = 0;
      this.failed = 0;
      this.skipped = 0;
      this.errors = [];

      try {
        const ProductIds = this.selectedProducts.map((row) => row.id);

        const response = await productService.bulkAdjustStock({
          ProductIds,
          newQty: this.newQty,
          reason: this.reason,
          notes: this.notes || "",
        });

        const data = response.data || response;

        this.processed = Number(data.processed || 0);
        this.failed = Number(data.failed || 0);
        this.skipped = Number(data.skipped || 0);
        this.errors = data.errors || [];

        this.$swal("Inventory", "Bulk adjustment completed.", "success");

        this.dialog = false;
        this.$emit("done", true);
      } catch (error) {
        console.error("bulk inventory adjustment failed", error);
        this.$swal("Inventory", "Bulk adjustment failed.", "error");
      } finally {
        this.working = false;
      }
    },
  },
};
</script>

<style scoped>
.bulk-adjust-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.summary-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 12px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 4px;
}

.field-label {
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  margin-bottom: 6px;
}

.progress-text {
  font-size: 13px;
  color: #64748b;
  margin-top: 8px;
}

.dialog-actions {
  padding: 12px 16px;
}

@media (max-width: 600px) {
  .summary-row {
    grid-template-columns: 1fr;
  }
}
</style>